/**
 * Throttle utility
 * 
 * Throttles function calls to ensure they don't execute more than once
 * per specified time interval. Queues the last arguments for execution
 * after the throttle period.
 */

/**
 * Throttle a function to execute at most once per `ms` milliseconds.
 * Queues the latest arguments for execution after the throttle period.
 */
export function throttle<T extends (...args: any[]) => void>(
  fn: T,
  ms: number
): T {
  let last = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let queuedArgs: Parameters<T> | null = null;

  const run = (args: Parameters<T>) => {
    last = Date.now();
    fn(...args);
  };

  return function (this: any, ...args: Parameters<T>) {
    const now = Date.now();
    const remaining = ms - (now - last);

    if (remaining <= 0) {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      run(args);
    } else {
      queuedArgs = args;
      if (!timer) {
        timer = setTimeout(() => {
          if (queuedArgs) {
            run(queuedArgs);
          }
          queuedArgs = null;
          timer = null;
        }, remaining);
      }
    }
  } as T;
}

